<template>
    <div class="container">
        <v-title :breadcrumb="breadcrumb">Search Results</v-title>

        <div class="grid" v-if="hasData && !isLoading">
            <div class="grid-item grid-item-full">
                <div class="tile">
                    <div class="tile-title">
                        <h2>Customers</h2>
                    </div>
                    <div class="tile-content" v-if="hasResults(fetchedData.customers)">
                        <v-repeater :list="fetchedData.customers" :slice="{value: sliceList, label: 'Customers'}">
                            <template slot="headers">
                                <th>Name</th>
                                <th>Email</th>
                                <th>Invoices</th>
                                <!--<th>Default Payment Instrument</th>-->
                                <th>Lifetime Spent</th>
                            </template>
                            <template slot="iteratee" slot-scope="{item}">
                                <td><router-link :to="{name: 'customer-details', params: {id: item.id}}">{{item.displayName}}</router-link></td>
                                <td>{{item.email | coverEmpty}}</td>
                                <td>{{item.invoiceCount | coverEmpty}}</td>
                                <!--<td>{{item.defaultPaymentInstrument.method | unKebabCase}}</td>-->
                                <td>{{item.lifetimeRevenue.amount | coverEmpty | monetizeExplicit(item.lifetimeRevenue.currency)}}</td>
                            </template>
                        </v-repeater>
                    </div>
                </div>
            </div>
            <div class="grid-item grid-item-full" v-if="hasResults(fetchedData.invoices)">
                <div class="tile">
                    <div class="tile-title">
                        <h2>Invoices</h2>
                    </div>
                    <div class="tile-content">
                        <v-repeater :list="fetchedData.invoices" :slice="{value: sliceList, label: 'Invoices'}">
                            <template slot="headers">
                                <th>Invoice<br/>Number</th>
                                <th>Products</th>
                                <th>Amount</th>
                                <th>Date Issued</th>
                                <th>Status</th>
                            </template>
                            <template slot="iteratee" slot-scope="{item}">
                                <td>
                                    <router-link :to="{name: 'invoice-details', params: {id: item.id}}">
                                        {{item.invoiceNumberLabel}}
                                    </router-link>
                                </td>
                                <td>{{item.productNames | join | coverEmpty}}</td>
                                <td>{{item.amount | monetizeExplicit(item.currency)}}</td>
                                <td>{{item.issuedTime | dateUTC('shortDate')}}</td>
                                <td><r-badge :type="getBadgeType('invoiceStatus', item.status)">{{item.statusLabel}}</r-badge></td>
                            </template>
                        </v-repeater>
                    </div>
                </div>
            </div>
            <div class="grid-item grid-item-full" v-if="hasResults(fetchedData.orders)">
                <div class="tile">
                    <div class="tile-title">
                        <h2>Orders</h2>
                    </div>
                    <div class="tile-content">
                        <v-repeater :list="fetchedData.orders" :slice="{value: sliceList, label: 'Orders'}">
                            <template slot="headers">
                                <th>Order</th>
                                <th>Date</th>
                                <th>Status</th>
                                <th>Type</th>
                                <th>Customer</th>
                                <th>Total</th>
                            </template>
                            <template slot="iteratee" slot-scope="{item}">
                                <td><router-link :to="{name: 'order-details', params: {id: item.id}}">{{item.id | truncate({limit: 26, dots: true})}}</router-link></td>
                                <td>{{item.createdDate | dateTime}}</td>
                                <td><r-badge :type="getBadgeType('orderStatus', item.status)">{{item.status | unKebabCase}}</r-badge></td>
                                <td><r-badge :type="getBadgeType('orderType', item.orderType)">{{item.orderType | unKebabCase}}</r-badge></td>
                                <td><router-link :to="{name: 'customer-details', params: {id: item.customerId}}">{{item.customerId | truncate({dots: true})}}</router-link></td>
                                <td>{{item.lineItemSubtotal.amount | monetizeExplicit(item.lineItemSubtotal.currency)}}</td>
                            </template>
                        </v-repeater>
                    </div>
                </div>
            </div>
            <div class="grid-item grid-item-full" v-if="hasResults(fetchedData.transactions)">
                <div class="tile">
                    <div class="tile-title">
                        <h2>Transactions</h2>
                    </div>
                    <div class="tile-content">
                        <v-repeater :list="fetchedData.transactions" :slice="{value: sliceList, label: 'Transactions'}">
                            <template slot="headers">
                                <th>Transaction</th>
                                <th>Status</th>
                                <th>Customer</th>
                                <th>Type</th>
                                <th>Result</th>
                                <th>Gateway Account</th>
                                <th>Processed On</th>
                                <th>Amount</th>
                            </template>
                            <template slot="iteratee" slot-scope="{item}">
                                <td><router-link :to="{name: 'transaction-details', params: {id: item.id}}">{{item.id | truncate({limit: 26, dots: true})}}</router-link></td>
                                <td><r-badge :type="getBadgeType('transactionStatus', item.status)">{{item.status | unKebabCase}}</r-badge></td>
                                <td><router-link :to="{name: 'customer-details', params: {id: item.customerId}}">{{item.customerId | truncate({dots: true})}}</router-link></td>
                                <td><r-badge :type="getBadgeType('transactionType', item.type)">{{item.type | unKebabCase}}</r-badge></td>
                                <td><r-badge :type="getBadgeType('transactionResult', item.result)">{{item.result | unKebabCase}}</r-badge></td>
                                <td>{{item.gatewayName}}</td>
                                <td>{{item.createdTime | dateTime}}</td>
                                <td>{{item.amount | monetizeExplicit(item.currency)}}</td>
                            </template>
                        </v-repeater>
                    </div>
                </div>
            </div>
        </div>

        <div v-else-if="isLoading">
            <div class="tile">
                <div class="tile-content padding-top-xl padding-bottom-xl position-relative">
                    <v-inline-loader />
                </div>
            </div>
        </div>

        <div class="tile" v-else>
            <div class="tile-content">
                <v-no-content>
                    <span slot="title">No Search Results</span>
                    <span slot="description">There are no results for your current search.</span>
                </v-no-content>
            </div>
        </div>
    </div>
</template>

<script>
    import vTitle from '@/components/v-title';
    import vRepeater from '@/components/repeater/repeater';
    import vNoContent from '@/components/v-no-content';
    import vInlineLoader from '@/components/v-inline-loader';
    import CustomerModel from '@/common/models/customer-model';
    import InvoiceModel from '@/common/models/invoice-model';
    import OrderModel from '@/common/models/order-model';
    import TransactionModel from '@/common/models/transaction-model';
    import {getBadgeType} from '@/data/badges-color-map';

    export default {
        components: {
            vTitle,
            vRepeater,
            vNoContent,
            vInlineLoader,
        },
        data() {
            return {
                isLoading: true,
                query: this.$route.query,
                fetchedData: {},
                sliceList: 5,
            };
        },
        computed: {
            hasData() {
                return Object.values(this.fetchedData).filter(result => result.length > 0).length > 0;
            },
            breadcrumb() {
                return {
                    label: 'Search Results',
                    route: {
                        name: 'search-results',
                        query: this.query,
                    },
                    root: true,
                };
            },
        },
        watch: {
            $route(newRoute, oldRoute) {
                if (newRoute.fullPath !== oldRoute.fullPath) {
                    this.query = this.$route.query;
                    this.fetchData();
                }
            },
        },
        methods: {
            getBadgeType,
            hasResults(list) {
                return list.length > 0;
            },
            async fetchData() {
                this.isLoading = true;
                try {
                    const {q} = this.query;
                    const {fields} = await this.$api(({search}) => search.get({q}));
                    let {
                        customers, invoices, orders, transactions,
                    } = fields;

                    customers = customers ? customers.map(customer => new CustomerModel(customer)) : [];
                    invoices = invoices ? invoices.map(invoice => new InvoiceModel(invoice)) : [];
                    orders = orders ? orders.map(order => new OrderModel(order)) : [];
                    transactions = transactions ? transactions.map(transaction => new TransactionModel(transaction)) : [];

                    this.fetchedData = {
                        customers, invoices, orders, transactions,
                    };
                } catch (error) {
                    this.$toast.fromError(error);
                } finally {
                    this.isLoading = false;
                }
            },
        },
        mounted() {
            this.fetchData();
        },
    };
</script>
